/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useJobQuoteDetails.ts
 * FILE_ID: JOBHUB-QA-QUOTE-DETAILS-HOOK-001
 * PURPOSE: Autosave job title, client, and ITP report title on the hub job.
 * WHY: The QA tab shares the ['job', routeId] cache so the header client chip updates.
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import type { Job, QuoteRow } from '../../jobDetailTypes'
import {
  defaultItpReportTitle,
  resolveItpReportTitle,
} from '../../qa/itpReportTitle'

type Flusher = () => Promise<string>
let flusher: Flusher | null = null

export function registerQuoteDetailsFlush(fn: Flusher): () => void {
  flusher = fn
  return () => {
    if (flusher === fn) flusher = null
  }
}

export async function flushPendingQuoteDetails(): Promise<string | null> {
  if (!flusher) return null
  return flusher()
}

function rowsOf(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>
  const results = (data as { results?: unknown[] } | null)?.results
  return Array.isArray(results) ? results as Array<Record<string, unknown>> : []
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Could not save quote details.'
}

export interface QuoteClientHit {
  id: number
  client_name?: string
  company_name?: string
  email?: string
  primary_email?: string
}

export interface QuoteSiteHit {
  id: number
  name?: string
  address?: unknown
}

type ParticularsJob = Job & { qa_quote_reference?: string | null }

export function displayedQuoteReference(
  saved: string | null | undefined,
  quotes: readonly { id: number; quote_number?: string | null }[],
  sourceIds: readonly number[],
): string {
  if (saved != null) return saved
  const first = sourceIds[0]
  if (first != null) {
    const match = quotes.find((row) => row.id === first)
    if (match?.quote_number) return match.quote_number
  }
  if (quotes.length === 1) return quotes[0]?.quote_number ?? ''
  return ''
}

// Reused from: packages/quote-builder/src/api/referenceApi.ts (searchSitesRanked, createSite, formatSiteAddress), same endpoints and ranking, not imported
function formatSiteAddress(address: unknown): string {
  if (!address) return ''
  if (typeof address === 'string') return address.trim()
  if (typeof address === 'object') {
    const row = address as Record<string, unknown>
    return [
      row.address_line1 ?? row.line1 ?? row.street ?? row.address,
      row.address_line2 ?? row.line2,
      row.city ?? row.suburb,
      row.state,
      row.postcode ?? row.postal_code ?? row.zip,
      row.country,
    ].map((part) => (part == null ? '' : String(part).trim())).filter(Boolean).join(', ')
  }
  return ''
}

export function siteLabel(hit: QuoteSiteHit): string {
  const name = (hit.name ?? '').trim()
  if (name) return name
  return formatSiteAddress(hit.address)
}

function siteHit(row: Record<string, unknown>): QuoteSiteHit | null {
  const id = Number(row.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: typeof row.name === 'string' ? row.name : undefined,
    address: row.address,
  }
}

async function fetchSites(
  client: { get: (url: string, config?: { params?: Record<string, unknown> }) => Promise<{ data: unknown }> },
  params: Record<string, unknown>,
): Promise<QuoteSiteHit[]> {
  const { data } = await client.get('/api/report/sites/', { params })
  return rowsOf(data).map(siteHit).filter((row): row is QuoteSiteHit => row != null)
}

export function useResolvedItpReportTitle(jobId: number): string {
  const { client } = useJobHubDataAdapter()
  const { data } = useQuery<Job>({
    queryKey: ['job', String(jobId)],
    queryFn: () => client.get(`/jobs/${jobId}`).then((response) => response.data),
  })
  return resolveItpReportTitle(data?.itp_report_title, data?.client_name)
}

export function useJobQuoteDetails(
  jobId: number,
  orgId?: number,
  quotes: readonly QuoteRow[] = [],
  sourceQuoteSheetIds: readonly number[] = [],
) {
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const { data: job } = useQuery<ParticularsJob>({
    queryKey: ['job', String(jobId)],
    queryFn: () => client.get(`/jobs/${jobId}`).then((response) => response.data),
  })
  const [jobDraft, setJobDraft] = useState('')
  const [titleDraft, setTitleDraft] = useState('')
  const [quoteDraft, setQuoteDraft] = useState('')
  const [siteDraft, setSiteDraft] = useState('')
  const [siteReset, setSiteReset] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [clientReset, setClientReset] = useState(0)
  const jobDraftRef = useRef('')
  const titleDraftRef = useRef('')
  const savedName = useRef<string | null>(null)
  const savedTitle = useRef<string | null>(null)
  const clientNameRef = useRef<string | null>(null)
  const loaded = useRef(false)
  const jobTimer = useRef<number | undefined>(undefined)
  const titleTimer = useRef<number | undefined>(undefined)
  const jobDirty = useRef(false)
  const titleDirty = useRef(false)
  const quoteDirty = useRef(false)
  const quoteDraftRef = useRef('')

  jobDraftRef.current = jobDraft
  titleDraftRef.current = titleDraft
  quoteDraftRef.current = quoteDraft
  clientNameRef.current = job?.client_name ?? null

  useEffect(() => {
    if (!job) return
    loaded.current = true
    savedName.current = job.display_name ?? null
    savedTitle.current = job.itp_report_title ?? null
    if (!jobDirty.current) setJobDraft(job.display_name ?? '')
    if (!titleDirty.current) {
      setTitleDraft(resolveItpReportTitle(job.itp_report_title, job.client_name))
    }
    if (!quoteDirty.current) {
      setQuoteDraft(displayedQuoteReference(job.qa_quote_reference, quotes, sourceQuoteSheetIds))
      setSiteDraft(job.site_address ?? '')
    }
  }, [job, quotes, sourceQuoteSheetIds])

  const publish = (next: Job) => {
    queryClient.setQueryData(['job', String(jobId)], next)
    queryClient.setQueryData(['job', jobId], next)
    void queryClient.invalidateQueries({ queryKey: ['jobs'] })
  }

  const commitJob = async (text: string) => {
    if (!loaded.current) return
    const next = text.trim() || null
    if ((savedName.current ?? null) === next) return
    const previous = savedName.current
    savedName.current = next
    jobDirty.current = false
    try {
      const { data } = await client.put<Job>(`/jobs/${jobId}`, { display_name: next })
      setError(null)
      publish(data)
    } catch (err) {
      savedName.current = previous
      jobDirty.current = false
      setJobDraft(previous ?? '')
      setError(messageOf(err))
    }
  }

  const commitTitle = async (text: string) => {
    if (!loaded.current) return
    const trimmed = text.trim()
    const fallback = defaultItpReportTitle(clientNameRef.current)
    const next = !trimmed || trimmed === fallback ? null : trimmed
    if ((savedTitle.current ?? null) === next) return
    const previous = savedTitle.current
    savedTitle.current = next
    titleDirty.current = false
    try {
      const { data } = await client.put<Job>(`/jobs/${jobId}`, { itp_report_title: next })
      setError(null)
      publish(data)
    } catch (err) {
      savedTitle.current = previous
      titleDirty.current = false
      setTitleDraft(resolveItpReportTitle(previous, clientNameRef.current))
      setError(messageOf(err))
    }
  }

  const commitJobRef = useRef(commitJob)
  const commitTitleRef = useRef(commitTitle)
  commitJobRef.current = commitJob
  commitTitleRef.current = commitTitle

  useEffect(() => {
    const flush = async () => {
      window.clearTimeout(jobTimer.current)
      window.clearTimeout(titleTimer.current)
      await commitJobRef.current(jobDraftRef.current)
      await commitTitleRef.current(titleDraftRef.current)
      return resolveItpReportTitle(savedTitle.current, clientNameRef.current)
    }
    return registerQuoteDetailsFlush(flush)
  }, [])

  const schedule = (kind: 'job' | 'title', text: string) => {
    const slot = kind === 'job' ? jobTimer : titleTimer
    window.clearTimeout(slot.current)
    slot.current = window.setTimeout(() => {
      void (kind === 'job' ? commitJobRef.current(text) : commitTitleRef.current(text))
    }, 400)
  }

  const orgParams = orgId != null ? { organisation_id: orgId } : {}

  return {
    jobTitle: jobDraft,
    reportTitle: titleDraft,
    clientName: job?.client_name ?? '',
    clientEmail: (job?.client_email ?? '').trim(),
    clientReset,
    error,
    resolvedTitle: resolveItpReportTitle(savedTitle.current ?? job?.itp_report_title, job?.client_name),
    setJobTitle: (text: string) => {
      jobDirty.current = true
      setJobDraft(text)
      schedule('job', text)
    },
    setReportTitle: (text: string) => {
      titleDirty.current = true
      setTitleDraft(text)
      schedule('title', text)
    },
    commitJobTitle: () => {
      window.clearTimeout(jobTimer.current)
      void commitJob(jobDraftRef.current)
    },
    commitReportTitle: () => {
      window.clearTimeout(titleTimer.current)
      void commitTitle(titleDraftRef.current)
    },
    quoteReference: quoteDraft,
    setQuoteReference: (text: string) => {
      quoteDirty.current = true
      setQuoteDraft(text)
    },
    commitQuoteReference: async () => {
      if (!quoteDirty.current || !loaded.current) return
      quoteDirty.current = false
      try {
        const { data } = await client.put<ParticularsJob>(
          `/api/qa/jobs/${jobId}/particulars/`,
          { qa_quote_reference: quoteDraftRef.current },
        )
        setError(null)
        publish({ ...(job as ParticularsJob), ...data })
      } catch (err) {
        quoteDirty.current = true
        setError(messageOf(err))
      }
    },
    siteName: siteDraft,
    siteReset,
    searchSites: async (query: string): Promise<QuoteSiteHit[]> => {
      const base = { ...orgParams, search: query }
      if (job?.client_id == null) return fetchSites(client, base)
      const [mine, all] = await Promise.all([
        fetchSites(client, { ...base, client: job.client_id }).catch(() => [] as QuoteSiteHit[]),
        fetchSites(client, base),
      ])
      const seen = new Set<number>()
      const ranked: QuoteSiteHit[] = []
      for (const hit of [...mine, ...all]) {
        if (seen.has(hit.id)) continue
        seen.add(hit.id)
        ranked.push(hit)
      }
      return ranked
    },
    createSite: async (name: string): Promise<QuoteSiteHit | null> => {
      const { data } = await client.post<Record<string, unknown>>('/api/report/sites/', {
        organisation_id: orgId,
        name,
      })
      return siteHit(data)
    },
    selectSite: async (hit: QuoteSiteHit) => {
      try {
        const { data } = await client.put<ParticularsJob>(
          `/api/qa/jobs/${jobId}/particulars/`,
          { site_id: hit.id },
        )
        setError(null)
        setSiteDraft(siteLabel(hit) || (typeof data.site_address === 'string' ? data.site_address : siteDraft))
        publish({ ...(job as ParticularsJob), ...data })
      } catch (err) {
        setSiteReset((value) => value + 1)
        setError(messageOf(err))
      }
    },
    searchClients: async (query: string): Promise<QuoteClientHit[]> => {
      const { data } = await client.get('/api/report/clients/', {
        params: { ...orgParams, search: query },
      })
      return rowsOf(data).map((row) => ({
        id: Number(row.id),
        client_name: typeof row.client_name === 'string' ? row.client_name : undefined,
        company_name: typeof row.company_name === 'string' ? row.company_name : undefined,
        email: typeof row.email === 'string' ? row.email : undefined,
        primary_email: typeof row.primary_email === 'string' ? row.primary_email : undefined,
      }))
    },
    createClient: async (name: string): Promise<QuoteClientHit | null> => {
      const { data } = await client.post<Record<string, unknown>>(
        '/api/report/clients/',
        { client_name: name },
        { params: orgParams },
      )
      const id = Number(data?.id)
      if (!Number.isFinite(id)) return null
      return {
        id,
        client_name: typeof data.client_name === 'string' ? data.client_name : name,
      }
    },
    selectClient: async (hit: QuoteClientHit) => {
      if (hit.id === job?.client_id) return
      try {
        const { data } = await client.put<Job>(`/jobs/${jobId}`, { client_id: hit.id })
        setError(null)
        titleDirty.current = false
        publish(data)
      } catch (err) {
        setClientReset((value) => value + 1)
        setError(messageOf(err))
      }
    },
    coverImageUrl: job?.itp_cover_image_url ?? null,
    uploadCover: async (file: File) => {
      const body = new FormData()
      body.append('itp_cover_image', file)
      try {
        const { data } = await client.put<Job>(`/jobs/${jobId}`, body)
        setError(null)
        publish(data)
      } catch (err) {
        setError(messageOf(err))
      }
    },
    clearCover: async () => {
      try {
        const { data } = await client.put<Job>(`/jobs/${jobId}`, { itp_cover_image: null })
        setError(null)
        publish(data)
      } catch (err) {
        setError(messageOf(err))
      }
    },
    setError,
  }
}
